import { useEffect, useState } from 'react';
import { adminBlogService, BlogPost } from '@/services/blog';

interface Message {
  type: 'success' | 'error' | 'info';
  text: string;
}

export default function BlogManagementView() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);

  const showMessage = (type: Message['type'], text: string) => {
    setMessages((prev) => [...prev, { type, text }]);
    setTimeout(() => setMessages((prev) => prev.slice(1)), 4500);
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await adminBlogService.listPosts({
        page,
        limit,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setPosts(res.posts);
      setTotal(res.total);
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [page, statusFilter]);

  const handleSearch = () => {
    setPage(1);
    loadPosts();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === posts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(posts.map((p) => p.id)));
    }
  };

  const handleBatchPublish = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await adminBlogService.batchPublish(Array.from(selectedIds));
      showMessage('success', res.message);
      setSelectedIds(new Set());
      loadPosts();
    } catch (error: any) {
      showMessage('error', error.message || 'Batch publish failed');
    }
  };

  const handleBatchUnpublish = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await adminBlogService.batchUnpublish(Array.from(selectedIds));
      showMessage('success', res.message);
      setSelectedIds(new Set());
      loadPosts();
    } catch (error: any) {
      showMessage('error', error.message || 'Batch unpublish failed');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} posts?`)) return;
    try {
      const res = await adminBlogService.batchDelete(Array.from(selectedIds));
      showMessage('success', res.message);
      setSelectedIds(new Set());
      loadPosts();
    } catch (error: any) {
      showMessage('error', error.message || 'Batch delete failed');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await adminBlogService.deletePost(postId);
      showMessage('success', 'Post deleted');
      if (selectedPost?.id === postId) setSelectedPost(null);
      loadPosts();
    } catch (error: any) {
      showMessage('error', error.message || 'Delete failed');
    }
  };

  const handlePublishPost = async (postId: string) => {
    try {
      await adminBlogService.publishPost(postId);
      showMessage('success', 'Post published');
      loadPosts();
    } catch (error: any) {
      showMessage('error', error.message || 'Publish failed');
    }
  };

  const handleUnpublishPost = async (postId: string) => {
    try {
      await adminBlogService.unpublishPost(postId);
      showMessage('success', 'Post unpublished');
      loadPosts();
    } catch (error: any) {
      showMessage('error', error.message || 'Unpublish failed');
    }
  };

  const formatDate = (ts?: number) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleDateString();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="admin-blog-management">
      <div className="admin-blog-header">
        <h1>Blog Management</h1>
        <p className="admin-text-muted">Manage blog posts</p>
      </div>

      {messages.length > 0 && (
        <div className="admin-messages">
          {messages.map((m, idx) => (
            <div key={idx} className={`admin-message admin-message--${m.type}`}>
              {m.text}
            </div>
          ))}
        </div>
      )}

      <div className="admin-blog-toolbar">
        <div className="admin-blog-search">
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>Search</button>
        </div>
        <div className="admin-blog-filters">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        {selectedIds.size > 0 && (
          <div className="admin-blog-batch-actions">
            <button onClick={handleBatchPublish}>Publish ({selectedIds.size})</button>
            <button onClick={handleBatchUnpublish}>Unpublish ({selectedIds.size})</button>
            <button onClick={handleBatchDelete} className="danger">Delete ({selectedIds.size})</button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="admin-loading">Loading...</div>
      ) : (
        <div className="admin-blog-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={posts.length > 0 && selectedIds.size === posts.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Title</th>
                <th>Status</th>
                <th>Category</th>
                <th>Published</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} className={selectedPost?.id === post.id ? 'selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(post.id)}
                      onChange={() => toggleSelect(post.id)}
                    />
                  </td>
                  <td onClick={() => setSelectedPost(post)} style={{ cursor: 'pointer' }}>
                    {post.title}
                  </td>
                  <td>
                    <span className={`status-badge status-badge--${post.status}`}>
                      {post.status}
                    </span>
                  </td>
                  <td>{post.category || '-'}</td>
                  <td>{formatDate(post.published_at)}</td>
                  <td>
                    {post.status === 'draft' ? (
                      <button className="admin-btn-sm" onClick={() => handlePublishPost(post.id)}>Publish</button>
                    ) : (
                      <button className="admin-btn-sm" onClick={() => handleUnpublishPost(post.id)}>Unpublish</button>
                    )}
                    <button className="admin-btn-sm danger" onClick={() => handleDeletePost(post.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr>
                  <td colSpan={6} className="admin-text-center">No posts found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}

      {selectedPost && (
        <div className="admin-post-detail-panel">
          <div className="admin-post-detail-header">
            <h2>{selectedPost.title}</h2>
            <button className="admin-close-btn" onClick={() => setSelectedPost(null)}>x</button>
          </div>
          <div className="admin-post-detail-body">
            <p><strong>Slug:</strong> {selectedPost.slug}</p>
            <p><strong>Status:</strong> {selectedPost.status}</p>
            <p><strong>Category:</strong> {selectedPost.category || '-'}</p>
            <p><strong>Tags:</strong> {selectedPost.tags?.join(', ') || '-'}</p>
            <p><strong>Read Time:</strong> {selectedPost.read_time} min</p>
            <p><strong>Excerpt:</strong> {selectedPost.excerpt || '-'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
